import { useEffect, useState } from "react";
import { useParams, useLocation, Link } from "react-router-dom";
import { CheckCircle2, ChefHat, Phone, Clock, Bike, ShoppingBag, Building2, Banknote } from "lucide-react";
import { Button } from "@/components/ui/button";
import api, { fmtMVR } from "@/lib/api";

const PAYMENT_LABELS = {
    cod: { label: "Cash on Delivery", icon: Banknote },
    pickup: { label: "Pay on Pickup", icon: Banknote },
    bank_transfer: { label: "Bank Transfer (BML)", icon: Building2 },
};

export default function ConfirmationPage() {
    const { orderNumber } = useParams();
    const location = useLocation();
    const [order, setOrder] = useState(location.state || null);

    useEffect(() => {
        if (!order && orderNumber) {
            api.get(`/orders/track/${orderNumber}`)
                .then((res) => setOrder(res.data))
                .catch(() => {});
        }
    }, [order, orderNumber]);

    const Icon = order ? PAYMENT_LABELS[order.payment_method]?.icon : null;

    return (
        <div
            className="min-h-screen bg-pz-bg paper-grain pb-12"
            data-testid="confirmation-page"
        >
            <header className="px-6 py-6 flex items-center justify-between max-w-4xl mx-auto">
                <Link to="/" className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-pz-primary text-white grid place-items-center">
                        <ChefHat size={16} />
                    </div>
                    <span className="font-display text-xl tracking-wide text-pz-text">
                        PIZANO
                    </span>
                </Link>
                <Link
                    to="/menu"
                    className="text-sm font-semibold text-pz-text hover:text-pz-primary uppercase tracking-wider"
                >
                    Order Again
                </Link>
            </header>

            <main className="max-w-2xl mx-auto px-4 sm:px-6">
                <div className="text-center mb-8 animate-float-up">
                    <div className="w-20 h-20 mx-auto rounded-full bg-pz-success/10 grid place-items-center mb-4">
                        <CheckCircle2 size={40} className="text-pz-success" />
                    </div>
                    <p className="uppercase text-xs tracking-[0.4em] text-pz-primary font-semibold mb-2">
                        Order Received
                    </p>
                    <h1 className="font-display text-4xl sm:text-5xl text-pz-text leading-tight">
                        Thanks for ordering!
                    </h1>
                    <p className="text-pz-textMuted mt-2">
                        Show this number at pickup or to the delivery rider.
                    </p>
                </div>

                {/* Big order number */}
                <div className="bg-white rounded-2xl border border-pz-border p-6 sm:p-10 text-center mb-6 relative overflow-hidden">
                    <div className="absolute inset-x-0 top-0 h-2 bg-pz-primary" />
                    <p className="uppercase text-xs tracking-widest text-pz-textMuted mb-3">
                        Your order number
                    </p>
                    <div
                        data-testid="order-number"
                        className="font-display text-7xl sm:text-8xl text-pz-primary leading-none"
                    >
                        #{orderNumber}
                    </div>
                    <div className="mt-4 inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-pz-warning/15 text-pz-warning border border-pz-warning/30">
                        <Clock size={14} />
                        <span className="text-xs font-bold uppercase tracking-wider">
                            Status: {order?.status || "pending"}
                        </span>
                    </div>
                </div>

                {order && (
                    <div className="bg-white rounded-2xl border border-pz-border p-5 sm:p-6 space-y-5">
                        <div className="grid sm:grid-cols-2 gap-5">
                            <div>
                                <p className="text-xs uppercase tracking-wider text-pz-textMuted font-semibold">
                                    Customer
                                </p>
                                <p className="font-semibold text-pz-text mt-1">
                                    {order.customer_name}
                                </p>
                                <p className="text-sm text-pz-textMuted">
                                    <Phone
                                        size={12}
                                        className="inline mr-1"
                                    />
                                    {order.phone}
                                </p>
                            </div>
                            <div>
                                <p className="text-xs uppercase tracking-wider text-pz-textMuted font-semibold">
                                    Order Type
                                </p>
                                <p className="font-semibold text-pz-text mt-1 inline-flex items-center gap-2">
                                    {order.order_type === "delivery" ? (
                                        <Bike size={14} />
                                    ) : (
                                        <ShoppingBag size={14} />
                                    )}
                                    {order.order_type === "delivery"
                                        ? "Delivery"
                                        : "Takeaway"}
                                </p>
                                {order.address && (
                                    <p className="text-sm text-pz-textMuted">
                                        {order.address}
                                    </p>
                                )}
                            </div>
                            <div className="sm:col-span-2">
                                <p className="text-xs uppercase tracking-wider text-pz-textMuted font-semibold">
                                    Payment
                                </p>
                                <p className="font-semibold text-pz-text mt-1 inline-flex items-center gap-2">
                                    {Icon && <Icon size={14} />}
                                    {PAYMENT_LABELS[order.payment_method]
                                        ?.label || order.payment_method}
                                </p>
                            </div>
                        </div>

                        <div className="border-t border-pz-border pt-4 space-y-2">
                            {order.items?.map((i) => (
                                <div
                                    key={i.id}
                                    className="flex justify-between text-sm"
                                >
                                    <span className="text-pz-text">
                                        <b>{i.quantity}×</b> {i.name}
                                    </span>
                                    <span className="text-pz-textMuted">
                                        {fmtMVR(i.price * i.quantity)}
                                    </span>
                                </div>
                            ))}
                            <div className="flex justify-between pt-3 mt-3 border-t border-pz-border">
                                <span className="font-semibold text-pz-text uppercase tracking-wider text-xs">
                                    Total
                                </span>
                                <span className="font-display text-2xl text-pz-primary">
                                    {fmtMVR(order.total)}
                                </span>
                            </div>
                        </div>
                    </div>
                )}

                <div className="mt-6 grid sm:grid-cols-2 gap-3">
                    <Link to="/menu" className="block">
                        <Button
                            variant="outline"
                            data-testid="confirmation-back-menu"
                            className="w-full h-12 rounded-full border-pz-border hover:bg-pz-bg2 text-pz-text"
                        >
                            Back to Menu
                        </Button>
                    </Link>
                    <Link to="/track" className="block">
                        <Button
                            data-testid="confirmation-track-btn"
                            className="w-full h-12 rounded-full bg-pz-text hover:bg-pz-primary text-white"
                        >
                            Track this order
                        </Button>
                    </Link>
                </div>
            </main>
        </div>
    );
}
